/* ==========================================================================
   Vinhuset filters (collection and search pages; markup: snippets/vh-filter-options.liquid)
   - One option can stand for several of Shopify's values: ticking it ticks them all (spellings, the alias list,
     alcohol bands); the Vivino steps tick every score from the step up.
   - "Flere …"/"Alle …" and the search field in long groups.
   - Region flags: the country is read from the wines (sections/vh-filter-regions.liquid), once per visit.
   - Chips of options with several values remove all of them.
   - Phones: the quick filters open one filter group in a sheet from the bottom.
   Broadcast's collection-filters-form still sends the form and swaps the sidebar and the grid after every change
   (assets/collection-filters-form.js); this only keeps the visible buttons and the real checkboxes together, and
   puts its own state (open groups, search text, the sheet) back after each swap.
   ========================================================================== */
(function () {
  'use strict';

  if (window.vhFilters) return;

  const SIDEBAR = '[data-collection-sidebar]';
  const SECTION = '[data-section-type="collection"]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // what the visitor opened or typed per group; Broadcast renders the groups again after every change
  const view = {open: new Set(), query: new Map()};

  const esc = (value) => (window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&'));
  // as Shopify's handleize: case, accents, Danish letters and punctuation don't matter when searching
  const fold = (text) =>
    String(text || '')
      .toLowerCase()
      .replace(/æ/g, 'ae')
      .replace(/ø/g, 'o')
      .replace(/å/g, 'a')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');

  /* ---------- One option, several values ---------- */

  // the Vivino steps are one choice: a step ticks every score from it and up, and unticks the other step
  function pickStep(input) {
    const box = input.closest('[data-vh-opts]');
    if (!box) return;
    const min = parseFloat(input.dataset.vhStep);
    box.querySelectorAll('[data-vh-step]').forEach((step) => {
      if (step !== input) step.checked = false;
      step.closest('[data-vh-opt]').classList.toggle('is-active', step.checked);
    });
    box.querySelectorAll('[data-vh-score]').forEach((value) => {
      value.checked = input.checked && parseFloat(value.dataset.vhScore) >= min - 0.001;
    });
  }

  // Broadcast listens for input events on the sidebar; a group shown in the sheet sits outside it. `typed`: a price
  // being typed, which waits for the pause (see "Results sooner")
  function sendFilters(section, typed) {
    const sidebar = section && section.querySelector(SIDEBAR);
    if (sidebar) sidebar.dispatchEvent(new CustomEvent('input', {bubbles: true, detail: {vhTyped: Boolean(typed)}}));
  }

  // capture phase: the hidden values follow the button before Broadcast (bubble phase, debounced) reads the form
  document.addEventListener(
    'input',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.matches('[data-vh-search]')) {
        // typing searches the group; it must not send the filters
        event.stopPropagation();
        search(input);
        return;
      }
      if (input.matches('[data-vh-main]')) {
        const option = input.closest('[data-vh-opt]');
        const box = option.closest('[data-vh-opts]');
        box.querySelectorAll(`[data-vh-extra][data-vh-for="${esc(option.dataset.vhKey)}"]`).forEach((extra) => {
          extra.checked = input.checked;
        });
        option.classList.toggle('is-active', input.checked);
      } else if (input.matches('[data-vh-step]')) {
        pickStep(input);
      }
      if (sheet.el && sheet.el.contains(input)) sendFilters(sheet.section, isTyped(input));
    },
    true
  );

  // Enter in a group's search field would send the whole form as a page load
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Enter' && event.target.matches && event.target.matches('[data-vh-search]')) event.preventDefault();
    },
    true
  );

  /* ---------- "Flere …"/"Alle …" and search ---------- */

  function setOpen(box, open) {
    box.classList.toggle('is-open', open);
    const more = box.querySelector('[data-vh-more]');
    if (more) {
      more.setAttribute('aria-expanded', open ? 'true' : 'false');
      const label = more.querySelector('[data-vh-more-label]');
      if (label) label.textContent = open ? more.dataset.labelLess : more.dataset.labelMore;
    }
    if (open) view.open.add(box.dataset.vhOpts);
    else view.open.delete(box.dataset.vhOpts);
  }

  function search(input) {
    const box = input.closest('[data-vh-opts]');
    if (!box) return;
    const query = fold(input.value);
    let hits = 0;
    box.querySelectorAll('[data-vh-opt]').forEach((option) => {
      const hit = !query || fold(option.dataset.vhLabel).includes(query);
      option.classList.toggle('is-miss', !hit);
      if (hit) hits += 1;
    });
    box.classList.toggle('is-searching', !!query);
    box.classList.toggle('is-empty', !!query && hits === 0);
    if (query) view.query.set(box.dataset.vhOpts, input.value);
    else view.query.delete(box.dataset.vhOpts);
  }

  // after Broadcast's swap: the groups the visitor opened stay open, the search text stays
  function restore(root) {
    root.querySelectorAll('[data-vh-opts]').forEach((box) => {
      const key = box.dataset.vhOpts;
      if (view.open.has(key)) setOpen(box, true);
      const text = view.query.get(key);
      const input = box.querySelector('[data-vh-search]');
      if (text && input) {
        input.value = text;
        search(input);
      }
    });
  }

  /* ---------- Region flags ---------- */

  let flags = null;
  let flagsLoading = null;

  // the page's own address without filters, sort or page: every wine of the collection (or search)
  function regionAddress(page) {
    const url = new URL(window.location.href);
    Array.from(url.searchParams.keys()).forEach((key) => {
      if (key.indexOf('filter.') === 0 || key === 'page' || key === 'sort_by' || key === 'section_id') url.searchParams.delete(key);
    });
    url.searchParams.set('section_id', 'vh-filter-regions');
    url.searchParams.set('page', String(page));
    return url;
  }

  function loadFlags() {
    if (flags) return Promise.resolve(flags);
    if (flagsLoading) return flagsLoading;
    const first = regionAddress(1);
    const key = `vh-filter-regions:${first.pathname}${first.searchParams.get('q') || ''}`;
    try {
      const kept = window.sessionStorage.getItem(key);
      if (kept) {
        flags = JSON.parse(kept);
        return Promise.resolve(flags);
      }
    } catch (error) {
      /* storage blocked: ask the shop */
    }
    flagsLoading = (async () => {
      const map = {};
      for (let page = 1; page <= 4; page += 1) {
        const response = await fetch(regionAddress(page), {credentials: 'same-origin'});
        if (!response.ok) break;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = doc.querySelector('[data-vh-regions]');
        if (!node) break;
        const data = JSON.parse(node.textContent);
        Object.assign(map, data.flags || {});
        if (!data.pages || page >= data.pages) break;
      }
      flags = map;
      try {
        window.sessionStorage.setItem(key, JSON.stringify(map));
      } catch (error) {
        /* storage full or blocked */
      }
      return map;
    })().catch(() => {
      flags = {};
      return flags;
    });
    return flagsLoading;
  }

  function paintFlags(root) {
    const slots = Array.from((root || document).querySelectorAll('[data-vh-region-flag]:not([data-vh-painted])'));
    if (!slots.length) return;
    loadFlags().then((map) => {
      slots.forEach((slot) => {
        if (slot.hasAttribute('data-vh-painted')) return;
        slot.setAttribute('data-vh-painted', '');
        const src = String(slot.dataset.vhValues || '')
          .split('|')
          .map((value) => map[value])
          .find(Boolean);
        if (!src) return;
        const img = document.createElement('img');
        img.className = 'vh-flag-icon';
        img.alt = '';
        img.width = 16;
        img.height = 16;
        img.decoding = 'async';
        img.addEventListener('error', () => img.remove());
        img.src = src;
        slot.appendChild(img);
      });
    });
  }

  /* ---------- Chips ---------- */

  // a chip for an option with several chosen values: untick the option (all its values), not just one value
  function removeChip(event, chip) {
    const section = chip.closest(SECTION);
    const scope = section || document;
    const box =
      (sheet.el && sheet.el.querySelector(`[data-vh-opts="${esc(chip.dataset.vhChip)}"]`)) ||
      scope.querySelector(`[data-vh-opts="${esc(chip.dataset.vhChip)}"]`);
    const option = box && box.querySelector(`[data-vh-opt][data-vh-key="${esc(chip.dataset.vhChipKey)}"]`);
    const input = option && option.querySelector('[data-vh-main], [data-vh-step]');
    if (!input) return; // the link itself removes one value
    event.preventDefault();
    input.checked = false;
    input.dispatchEvent(new Event('input', {bubbles: true}));
  }

  /* ---------- Quick filters on phones: one group in a sheet from the bottom ---------- */

  const phone = window.matchMedia('(max-width: 749px)');

  const sheet = {
    el: null,
    key: '',
    section: null,
    content: null,
    mark: null,

    build() {
      if (this.el) return this.el;
      const quick = document.querySelector('[data-vh-quick]');
      const labels = (quick && quick.dataset) || {};
      const el = document.createElement('div');
      el.className = 'vh-sheet';
      el.hidden = true;
      el.setAttribute('data-vh-sheet', '');
      el.innerHTML = `
        <div class="vh-sheet__backdrop" data-vh-sheet-close></div>
        <div class="vh-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="vh-sheet-title">
          <div class="vh-sheet__head" data-vh-sheet-drag>
            <span class="vh-sheet__grab" aria-hidden="true"></span>
            <div class="vh-sheet__bar">
              <h2 class="vh-sheet__title" id="vh-sheet-title" tabindex="-1"></h2>
              <button type="button" class="vh-sheet__close" data-vh-sheet-close>
                <svg aria-hidden="true" focusable="false" class="icon icon-cancel" viewBox="0 0 24 24"><path d="M6.758 17.243 12.001 12m5.243-5.243L12 12m0 0L6.758 6.757M12.001 12l5.243 5.243" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
          </div>
          <div class="vh-sheet__body" data-vh-sheet-body></div>
          <div class="vh-sheet__foot">
            <button type="button" class="btn btn--outline btn--small vh-sheet__clear" data-vh-sheet-clear></button>
            <button type="button" class="btn btn--primary btn--solid btn--small vh-sheet__done" data-vh-sheet-done></button>
          </div>
        </div>`;
      el.querySelector('.vh-sheet__close').setAttribute('aria-label', labels.labelClose || 'Luk');
      el.querySelector('[data-vh-sheet-clear]').textContent = labels.labelClear || 'Ryd';
      document.body.appendChild(el);
      el.addEventListener('keydown', (event) => this.keydown(event));
      // Broadcast's price slider reports to the sidebar's form; in the sheet the report comes here
      el.addEventListener('theme:range:update', (event) => this.range(event));
      this.dragToClose(el);
      this.el = el;
      return el;
    },

    group() {
      const sidebar = this.section && this.section.querySelector(SIDEBAR);
      return sidebar && sidebar.querySelector(`[data-vh-group="${esc(this.key)}"]`);
    },

    open(key, opener) {
      const section = opener.closest(SECTION);
      if (!section) return;
      this.build();
      if (!this.el.hidden) this.giveBack();
      this.key = key;
      this.section = section;
      const group = this.group();
      if (!group) return;
      this.el.querySelector('.vh-sheet__title').textContent = group.dataset.vhTitle || opener.textContent.trim();
      this.el.hidden = false; // visible before the group moves in: the price slider measures itself on arrival
      this.adopt(group);
      this.showCount();
      document.documentElement.classList.add('vh-sheet-open');
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
      requestAnimationFrame(() => {
        this.el.classList.add('is-open');
        this.el.querySelector('.vh-sheet__title').focus({preventScroll: true});
      });
    },

    // the group's content moves into the sheet (its inputs name the filter form, so they still count), and a
    // marker keeps its place in the drawer
    adopt(group) {
      const content = group.querySelector('[data-collapsible-content]');
      if (!content) return;
      this.mark = document.createComment('vh-sheet');
      content.parentNode.insertBefore(this.mark, content);
      this.el.querySelector('[data-vh-sheet-body]').appendChild(content);
      this.content = content;
      restore(content);
      paintFlags(content);
    },

    giveBack() {
      if (this.content && this.mark && this.mark.parentNode) {
        this.mark.parentNode.insertBefore(this.content, this.mark);
        this.mark.remove();
      } else if (this.content) {
        this.content.remove();
      }
      this.content = null;
      this.mark = null;
    },

    close() {
      if (!this.el || this.el.hidden) return;
      const key = this.key;
      const section = this.section;
      this.el.classList.remove('is-open', 'is-busy');
      document.documentElement.classList.remove('vh-sheet-open');
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      const finish = () => {
        if (this.el.classList.contains('is-open')) return; // opened again meanwhile
        this.el.hidden = true;
        this.giveBack();
      };
      if (reducedMotion) finish();
      else setTimeout(finish, 300);
      const opener = section && section.querySelector(`[data-vh-quick-open="${esc(key)}"]`);
      if (opener) opener.focus({preventScroll: true});
    },

    // Broadcast rendered the sidebar again: its copy of the group is current, the one in the sheet is stale
    // (and its inputs would be sent twice), so the new one moves in
    refresh() {
      if (!this.el || this.el.hidden || !this.content) return;
      const body = this.el.querySelector('[data-vh-sheet-body]');
      const top = body.scrollTop;
      this.content.remove();
      this.content = null;
      this.mark = null;
      const group = this.group();
      if (!group) {
        this.close();
        return;
      }
      this.adopt(group);
      body.scrollTop = top;
      this.showCount();
      this.el.classList.remove('is-busy');
    },

    // the same text as the drawer's "Vis 51 produkter", which Broadcast keeps up to date
    showCount() {
      const source = this.section && this.section.querySelector('[data-vh-filters-done]');
      const done = this.el.querySelector('[data-vh-sheet-done]');
      if (source && done) done.textContent = source.textContent.trim();
    },

    clear() {
      if (!this.content) return;
      let changed = false;
      this.content.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        if (input.checked) {
          input.checked = false;
          changed = true;
        }
      });
      this.content.querySelectorAll('.is-active').forEach((option) => option.classList.remove('is-active'));
      this.content.querySelectorAll('[data-field-price-min], [data-field-price-max]').forEach((input) => {
        if (input.value !== '') {
          input.value = '';
          changed = true;
        }
      });
      if (changed) sendFilters(this.section);
    },

    range(event) {
      const slider = event.target.closest('range-slider');
      const holder = event.target.closest('[data-range-holder]');
      if (slider && holder) {
        // both ends back where they started: no price filter, as Broadcast does it in the drawer
        const atMin = slider.getAttribute('data-se-min-value') === slider.getAttribute('data-se-min');
        const atMax = slider.getAttribute('data-se-max-value') === slider.getAttribute('data-se-max');
        if (atMin && atMax) {
          holder.querySelectorAll('[data-field-price-min], [data-field-price-max]').forEach((input) => {
            input.value = '';
          });
        }
      }
      sendFilters(this.section);
    },

    keydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        this.el.querySelectorAll('button, [href], input:not([type="hidden"]):not([hidden]), select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((node) => !node.disabled && node.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !this.el.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    // pull the head down to close, like other sheets on the phone
    dragToClose(el) {
      const head = el.querySelector('[data-vh-sheet-drag]');
      const panel = el.querySelector('.vh-sheet__panel');
      let startY = null;
      let dy = 0;
      head.addEventListener(
        'touchstart',
        (event) => {
          if (event.target.closest('button')) return;
          startY = event.touches[0].clientY;
          dy = 0;
          el.classList.add('is-dragging');
        },
        {passive: true}
      );
      head.addEventListener(
        'touchmove',
        (event) => {
          if (startY === null) return;
          dy = Math.max(0, event.touches[0].clientY - startY);
          panel.style.transform = `translateY(${dy}px)`;
        },
        {passive: true}
      );
      const end = () => {
        if (startY === null) return;
        startY = null;
        el.classList.remove('is-dragging');
        panel.style.transform = '';
        if (dy > 90) this.close();
      };
      head.addEventListener('touchend', end);
      head.addEventListener('touchcancel', end);
    },
  };

  /* ---------- The focus through Broadcast's swap ---------- */

  // where a keyboard (or screen reader) user was in the filters: Broadcast replaces the whole sidebar after every
  // change, and the focus would otherwise fall back to the start of the page
  let lastFocus = null;

  function describe(el) {
    const box = el.closest('[data-vh-opts]');
    const group = box && box.dataset.vhOpts;
    if (group && el.matches('[data-vh-search]')) return `[data-vh-opts="${esc(group)}"] [data-vh-search]`;
    if (group && el.matches('[data-vh-more]')) return `[data-vh-opts="${esc(group)}"] [data-vh-more]`;
    const option = el.closest('[data-vh-opt]');
    if (group && option) return `[data-vh-opts="${esc(group)}"] [data-vh-key="${esc(option.dataset.vhKey)}"] .vh-opt__input`;
    if (el.name && el.name.indexOf('filter.') === 0) return `[name="${esc(el.name)}"]`;
    return null;
  }

  document.addEventListener('focusin', (event) => {
    const el = event.target;
    lastFocus = el instanceof Element && el.closest(`${SIDEBAR}, [data-vh-sheet]`) ? describe(el) : null;
  });

  function restoreFocus(root) {
    if (!lastFocus || !root) return;
    const active = document.activeElement;
    if (active && active !== document.body && document.contains(active)) return; // the visitor has moved on
    const el = root.querySelector(lastFocus);
    if (!el) return;
    el.focus({preventScroll: true});
    try {
      if (el.matches('[data-vh-search]')) el.setSelectionRange(el.value.length, el.value.length);
    } catch (error) {
      /* not a text field */
    }
  }

  /* ---------- Clicks ---------- */

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const more = target.closest('[data-vh-more]');
    if (more) {
      const box = more.closest('[data-vh-opts]');
      if (box) setOpen(box, !box.classList.contains('is-open'));
      return;
    }

    const chip = target.closest('[data-vh-chip]');
    if (chip) {
      removeChip(event, chip);
      return;
    }

    const quick = target.closest('[data-vh-quick-open]');
    if (quick) {
      event.preventDefault();
      sheet.open(quick.dataset.vhQuickOpen, quick);
      return;
    }

    if (!sheet.el || !sheet.el.contains(target)) return;
    if (target.closest('[data-vh-sheet-close]') || target.closest('[data-vh-sheet-done]')) sheet.close();
    else if (target.closest('[data-vh-sheet-clear]')) sheet.clear();
  });

  /* ---------- Computer: the page scrolls before the filter column does ---------- */

  // The filter column sticks under the header and scrolls on its own. Until it has reached the header, a scroll over
  // it moves the page, so the count and "Sortér efter" above it go along instead of standing still over a list that
  // slides under them. Once the column is stuck, its list scrolls; at the top of the list the page takes over again.
  const wide = window.matchMedia('(min-width: 750px)');

  function watchColumn(section) {
    const column = section.querySelector(SIDEBAR);
    if (!column) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      const list = column.querySelector('.collection__sidebar');
      if (!list) return;
      let wait = false;
      if (wide.matches && list.scrollTop === 0) {
        const top = parseFloat(window.getComputedStyle(column).top) || 0;
        wait = column.getBoundingClientRect().top > top + 1;
      }
      list.classList.toggle('vh-list-waits', wait);
    };
    const schedule = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', schedule, {passive: true});
    window.addEventListener('resize', schedule, {passive: true});
    column.addEventListener('scroll', schedule, {passive: true, capture: true}); // the list's own scroll
    column._vhColumn = schedule;
    update();
  }

  /* ---------- Results sooner ---------- */
  // Broadcast's collection-filters-form sends the filters 500 ms after a change, fetches the whole page for them (about
  // 770 kB), shows nothing while it waits and keeps its loading state 500 ms after the swap. Its swap stays; around it:
  // - a tick (a checkbox, the sheet's buttons, the slider) is sent after 150 ms; a price being typed still waits 500 ms
  // - only the section is fetched (Section Rendering API, about 310 kB): it holds the count, the grid and the sidebar,
  //   which is all Broadcast swaps
  // - an answer is kept for the page view (unticking, back), and only the latest change's answer is swapped in, so an
  //   earlier, slower answer can't undo a later tick
  // - the products dim and the loading line shows from the request until the swap (vh-filtering, vh-collection.css)

  const TICK_WAIT = 150;
  const ANSWERS_MAX = 16;
  const answers = new Map(); // section address → its HTML (a promise)
  let wanted = null; // the address whose answer may be swapped in

  function isTyped(el) {
    return el instanceof HTMLInputElement && el.type !== 'checkbox' && el.type !== 'radio';
  }

  // the address Broadcast asks for, as its section alone
  function sectionAddress(form, url) {
    const id = form.container && form.container.dataset.sectionId;
    if (!id) return null;
    let address;
    try {
      address = new URL(url, window.location.href);
    } catch (error) {
      return null;
    }
    if (address.origin !== window.location.origin) return null;
    address.searchParams.set('section_id', id);
    return `${address.pathname}${address.search}`;
  }

  function fetchSection(address) {
    if (!answers.has(address)) {
      const answer = fetch(address, {credentials: 'same-origin'}).then((response) => {
        if (!response.ok) throw new Error(`filters ${response.status}`);
        return response.text();
      });
      answer.catch(() => answers.delete(address));
      answers.set(address, answer);
      if (answers.size > ANSWERS_MAX) answers.delete(answers.keys().next().value);
    }
    return answers.get(address);
  }

  // Broadcast reads every change from its sidebar's input events, debounced 500 ms: a tick goes sooner. Any change
  // makes an answer still on its way out of date.
  function quicken(form) {
    if (form._vhQuick || typeof form.updatePrice !== 'function' || typeof form.updatePriceEvent !== 'function') return;
    form._vhQuick = true;
    const typed = form.updatePriceEvent;
    let timer = 0;
    form.updatePriceEvent = (event) => {
      wanted = null;
      clearTimeout(timer);
      const detail = event.detail && typeof event.detail === 'object' ? event.detail : null;
      if (detail ? detail.vhTyped : isTyped(event.target)) typed(event);
      else timer = setTimeout(() => form.updatePrice(event), TICK_WAIT);
    };
  }

  // a form Broadcast has connected listens with its own wait: that listener is swapped for the quicker one
  function quickenConnected(root) {
    root.querySelectorAll('collection-filters-form').forEach((form) => {
      if (form._vhQuick || !form.collectionSidebar || !form.form || typeof form.updatePriceEvent !== 'function') return;
      form.collectionSidebar.removeEventListener('input', form.updatePriceEvent);
      quicken(form);
      form.collectionSidebar.addEventListener('input', form.updatePriceEvent);
    });
  }

  function speedUp() {
    const Form = window.customElements && customElements.get('collection-filters-form');
    if (!Form || Form.prototype._vhFast || typeof Form.prototype.submitForm !== 'function') return;
    const proto = Form.prototype;
    proto._vhFast = true;
    const submit = proto.submitForm;
    // Broadcast fetches the page's new address once, right away: that fetch gets the section instead
    proto.submitForm = function (...args) {
      const form = this;
      const realFetch = window.fetch;
      window.fetch = function (input, init) {
        window.fetch = realFetch;
        const url = typeof input === 'string' ? input : input && input.url;
        const address = url ? sectionAddress(form, url) : null;
        if (!address) return realFetch.call(window, input, init);
        wanted = address;
        const grid = form.productsContainer;
        if (grid) grid.classList.add('vh-filtering');
        return fetchSection(address).then(
          // a later change is on its way: Broadcast's chain stops here, without a swap
          (html) => (wanted === address ? new Response(html, {headers: {'Content-Type': 'text/html'}}) : new Promise(() => {})),
          (error) => {
            // no answer (offline): the products stay as they were, usable
            if (wanted === address && grid) grid.classList.remove('vh-filtering', 'is-loading');
            throw error;
          }
        );
      };
      try {
        return submit.apply(this, args);
      } finally {
        window.fetch = realFetch;
      }
    };
    document.querySelectorAll(SIDEBAR).forEach((sidebar) => {
      quickenConnected(sidebar);
      // Broadcast's swap brings a new form with the sidebar
      new MutationObserver(() => quickenConnected(sidebar)).observe(sidebar, {childList: true});
    });
    quickenConnected(document);
    // the swap ends the wait (Broadcast keeps is-loading 500 ms longer)
    document.querySelectorAll('[data-products-grid]').forEach((grid) => {
      new MutationObserver(() => grid.classList.remove('vh-filtering', 'is-loading')).observe(grid, {childList: true});
    });
  }

  /* ---------- After Broadcast's swap ---------- */

  function watch(section) {
    if (section._vhFilters) return;
    section._vhFilters = true;
    const sidebar = section.querySelector(SIDEBAR);
    const grid = section.querySelector('[data-products-grid]');
    if (sidebar) {
      new MutationObserver(() => {
        restore(sidebar);
        paintFlags(sidebar);
        if (sheet.section === section) sheet.refresh();
        restoreFocus(sheet.el && !sheet.el.hidden && sheet.section === section ? sheet.el : sidebar);
        if (sidebar._vhColumn) sidebar._vhColumn();
      }).observe(sidebar, {childList: true});
    }
    // while Broadcast fetches the new state, the sheet waits (a tick then would be lost in the swap)
    if (grid) {
      new MutationObserver(() => {
        if (!sheet.el || sheet.el.hidden || sheet.section !== section) return;
        sheet.el.classList.toggle('is-busy', grid.classList.contains('is-loading'));
      }).observe(grid, {attributes: true, attributeFilter: ['class']});
    }
  }

  function init() {
    if (window.customElements) customElements.whenDefined('collection-filters-form').then(speedUp);
    document.querySelectorAll(SECTION).forEach(watch);
    document.querySelectorAll(SECTION).forEach(watchColumn);
    document.querySelectorAll(SIDEBAR).forEach(restore);
    // the region flags are fetched once the page has settled (one small request, kept for the visit)
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 800));
    idle(() => paintFlags(document), {timeout: 1500});
    const leavePhone = (event) => {
      if (!event.matches) sheet.close();
    };
    if (phone.addEventListener) phone.addEventListener('change', leavePhone);
    else if (phone.addListener) phone.addListener(leavePhone);
  }

  window.vhFilters = {sheet, paintFlags};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
